<template>
  <div v-if="totalPages > 1" class="console-pagination">
    <button
      class="button button--ghost"
      :disabled="currentPage <= 1"
      @click="emitNavigate(currentPage - 1)"
    >
      Previous
    </button>
    <div class="console-pagination__pages" aria-label="Pagination">
      <template v-for="item in paginationItems" :key="item.key">
        <span v-if="item.type === 'ellipsis'" class="console-pagination__ellipsis" aria-hidden="true">
          ...
        </span>
        <button
          v-else
          type="button"
          class="console-pagination__page"
          :class="{ 'console-pagination__page--active': item.page === currentPage }"
          :aria-current="item.page === currentPage ? 'page' : undefined"
          @click="emitNavigate(item.page)"
        >
          {{ item.page }}
        </button>
      </template>
    </div>
    <span class="console-pagination__label">Page {{ currentPage }} of {{ totalPages }}</span>
    <button
      class="button button--ghost"
      :disabled="currentPage >= totalPages"
      @click="emitNavigate(currentPage + 1)"
    >
      Next
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  totalPages: {
    type: Number,
    required: true,
  },
})

const emit = defineEmits(['navigate'])

const paginationItems = computed(() => {
  const total = props.totalPages
  let pages = []

  if (total <= 7) {
    pages = Array.from({ length: total }, (_, index) => index + 1)
  } else if (props.currentPage <= 4) {
    pages = [1, 2, 3, 4, 5, total]
  } else if (props.currentPage >= total - 3) {
    pages = [1, total - 4, total - 3, total - 2, total - 1, total]
  } else {
    pages = [
      1,
      props.currentPage - 2,
      props.currentPage - 1,
      props.currentPage,
      props.currentPage + 1,
      props.currentPage + 2,
      total,
    ]
  }

  const items = []

  for (let index = 0; index < pages.length; index += 1) {
    const page = pages[index]
    const previousPage = pages[index - 1]

    if (previousPage && page - previousPage > 1) {
      items.push({ type: 'ellipsis', key: `ellipsis-${previousPage}-${page}` })
    }

    items.push({ type: 'page', key: `page-${page}`, page })
  }

  return items
})

function emitNavigate(page) {
  if (page < 1 || page > props.totalPages || page === props.currentPage) return
  emit('navigate', page)
}
</script>

<style scoped>
.console-pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
}

.console-pagination__pages {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.console-pagination__page {
  min-width: 40px;
  min-height: 40px;
  border: 1px solid var(--shell-border);
  background: var(--shell-surface-strong);
  color: var(--shell-text);
  font: inherit;
}

.console-pagination__page:hover {
  border-color: rgba(139, 78, 45, 0.28);
}

.console-pagination__page--active {
  border-color: rgba(139, 78, 45, 0.42);
  background: rgba(139, 78, 45, 0.12);
  font-weight: 700;
}

.console-pagination__page:focus-visible {
  outline: 2px solid rgba(139, 78, 45, 0.5);
  outline-offset: 2px;
}

.console-pagination__ellipsis {
  color: var(--shell-text-muted);
  letter-spacing: 0.12em;
}

.console-pagination__label {
  color: var(--shell-text-muted);
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .console-pagination {
    flex-direction: column;
  }
}
</style>
